<script lang="ts" setup>
import {
  PhFloppyDisk,
  PhListBullets,
  PhListNumbers,
  PhNote,
  PhPencilSimple,
  PhTextB,
  PhTextItalic,
  PhTextTSlash,
  PhTextUnderline,
  PhX,
} from '@phosphor-icons/vue';
import { useI18n } from 'vue-i18n';
import { useProjectNotesLogic } from './logic/useProjectNotesLogic';
import type { ProjectNotesProps } from './types';

const { t } = useI18n();

const props = defineProps<ProjectNotesProps>();
const emit = defineEmits<{ save: [notes: string] }>();
const { state, action, editorRef } = useProjectNotesLogic(props, emit);
</script>

<template>
  <div class="card">
    <div class="card-header">
      <p class="card-header-title">
        <PhNote :size="18" class="mr-2" />
        {{ t('overview.projectNotes.title') }}
      </p>
      <button
        v-if="state.isEditing"
        class="header-btn cancel-btn"
        @click="action.cancel()"
      >
        <PhX :size="15" />
        {{ t('overview.projectNotes.cancelButton') }}
      </button>
      <button
        v-if="state.mode === 'edit'"
        class="header-btn edit-btn"
        :class="{ 'is-active': state.isEditing }"
        @click="action.toggleEdit()"
      >
        <PhFloppyDisk v-if="state.isEditing" :size="15" />
        <PhPencilSimple v-else :size="15" />
        {{
          state.isEditing
            ? t('overview.projectNotes.saveButton')
            : t('overview.projectNotes.editButton')
        }}
      </button>
    </div>

    <Transition name="toolbar-slide">
      <div v-if="state.isEditing" class="notes-toolbar">
        <button
          type="button"
          class="toolbar-btn"
          @click="action.formatText('b')"
        >
          <PhTextB :size="16" />
        </button>
        <button
          type="button"
          class="toolbar-btn"
          @click="action.formatText('i')"
        >
          <PhTextItalic :size="16" />
        </button>
        <button
          type="button"
          class="toolbar-btn"
          @click="action.formatText('u')"
        >
          <PhTextUnderline :size="16" />
        </button>
        <button
          type="button"
          class="toolbar-btn"
          @click="action.insertList('ol')"
        >
          <PhListNumbers :size="16" />
        </button>
        <button
          type="button"
          class="toolbar-btn"
          @click="action.insertList('ul')"
        >
          <PhListBullets :size="16" />
        </button>
        <button
          type="button"
          class="toolbar-btn"
          @click="action.removeFormatting()"
        >
          <PhTextTSlash :size="16" />
        </button>
      </div>
    </Transition>

    <div class="card-content">
      <template v-if="state.isEditing">
        <div
          ref="editorRef"
          class="notes-editor"
          contenteditable="true"
          @input="action.onInput()"
        ></div>
      </template>
      <template v-else>
        <div v-if="state.notes" class="notes-content" v-html="state.notes" />
        <p v-else class="notes-empty">
          {{ t('overview.projectNotes.emptyMessage') }}
        </p>
      </template>
    </div>
  </div>
</template>

<style scoped>
.card {
  display: flex;
  flex-direction: column;
  max-height: 30rem;
}

.card-content {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 1;
}

.header-btn {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  align-self: center;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: #f5f5f7;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.edit-btn.is-active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.cancel-btn {
  margin-right: 0.4rem;
}

.cancel-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.toolbar-slide-enter-active,
.toolbar-slide-leave-active {
  transition:
    max-height 0.2s ease,
    opacity 0.2s ease;
  overflow: hidden;
  max-height: 3rem;
}

.toolbar-slide-enter-from,
.toolbar-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.notes-toolbar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4rem 0.75rem;
  background: #f0f0f0;
  border-bottom: 1px solid #e0e0e0;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem;
  background: #fff;
  border: none;
  border-radius: 0.2rem;
  cursor: pointer;
  color: #333;
}

.toolbar-btn:hover {
  background: #e0e0e0;
}

.notes-editor {
  min-height: 8rem;
  outline: none;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.6;
}

.notes-editor :deep(ol),
.notes-editor :deep(ul) {
  padding-left: 1.5rem;
}

.notes-editor :deep(ul) {
  list-style-type: disc;
}

.notes-content {
  font-size: 0.9rem;
  line-height: 1.6;
}

.notes-content :deep(ol),
.notes-content :deep(ul) {
  padding-left: 1.5rem;
}

.notes-content :deep(ul) {
  list-style-type: disc;
}

.notes-empty {
  color: #aaa;
  font-style: italic;
  font-weight: 400;
}
</style>
